import { useState, useEffect } from "react"
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react"
import {
  XMarkIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PaperClipIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { Fragment } from "react"
import { Button, ScrollShadow, Avatar, Badge, Spinner } from "components/ui"
import { Input, Textarea } from "components/ui/Form"
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/react"
import { getTemplates, extractTemplateVariables, replaceTemplateVariables, htmlToPlainText } from "services/templateService"
import { sendEmailToLead, sendEmailToMultipleLeads } from "services/leadsService"
import { getIntegrationsByType } from "services/integrationService"
import { RichTextEditor } from "components/shared/RichTextEditor"
import { EmailServiceSelector } from "components/shared/EmailServiceSelector"
import { toast } from "sonner"
import { ensureCompleteEmailHtml } from "utils/emailHtmlWrapper"

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Safe iframe-based HTML preview to avoid leaking template CSS into app
const IframePreview = ({ html, height = 160 }) => {
  const srcDoc = `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <style>
        html, body { margin: 0; padding: 0; background: transparent; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; color: #111827; font-size: 12px; line-height: 1.35; }
        h1 { font-size: 1rem; }
        h2 { font-size: 0.95rem; }
        h3 { font-size: 0.9rem; }
        a { color: #2563EB; }
        * { box-sizing: border-box; max-width: 100%; }
      </style>
    </head>
    <body>${html || ""}</body>
  </html>`

  return (
    <iframe
      title="email-template-preview"
      srcDoc={srcDoc}
      sandbox="allow-popups-to-escape-sandbox"
      className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-transparent"
      style={{ height }}
    />
  )
}

// Email Chips Input Component
const EmailChipsInput = ({ label, emails, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState("")
  const [inputError, setInputError] = useState("")

  const addEmail = (email) => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) return

    if (!isValidEmail(trimmedEmail)) {
      setInputError("Please enter a valid email address")
      return
    }

    if (emails.includes(trimmedEmail)) {
      setInputError("Email address already added")
      return
    }

    onChange([...emails, trimmedEmail])
    setInputValue("")
    setInputError("")
  }

  const removeEmail = (emailToRemove) => {
    onChange(emails.filter(email => email !== emailToRemove))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      removeEmail(emails[emails.length - 1])
    }
  }

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className={`min-h-[42px] p-2 border rounded-lg bg-white dark:bg-gray-800 transition-colors ${inputError
        ? 'border-red-300 dark:border-red-600 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
        : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'
        }`}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Email Chips */}
          {emails.map((email, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-700 transition-all duration-200 animate-in fade-in slide-in-from-left-2"
            >
              <EnvelopeIcon className="w-3.5 h-3.5 opacity-70" />
              <span className="max-w-[200px] truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="ml-0.5 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Remove email"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ))}

          {/* Input Field */}
          <input
            type="email"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setInputError("")
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={emails.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {inputError && (
        <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Press Enter or comma to add • Backspace to remove
        </p>
        {emails.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {emails.length} email{emails.length !== 1 ? 's' : ''} added
          </span>
        )}
      </div>
    </div>
  )
}

const EmailMessageModal = ({ isOpen, onClose, leadData, selectedLeads = null, initialAttachments = [] }) => {
  // Simple states for email sending
  const [activeTab, setActiveTab] = useState(0) // 0 = Compose, 1 = Templates
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [templateVariables, setTemplateVariables] = useState({})
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateSearch, setTemplateSearch] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [showHtmlEditor, setShowHtmlEditor] = useState(false)

  // Email service selection states
  const [selectedEmailService, setSelectedEmailService] = useState(null)
  const [showEmailServiceSelector, setShowEmailServiceSelector] = useState(false)

  // Email form data
  const [emailData, setEmailData] = useState({
    to: leadData?.email || "",
    toEmails: [], // For bulk mode - array of email addresses
    cc: [],
    bcc: [],
    subject: "",
    body: "",
    attachments: [] // Array of file objects or URLs
  })

  // Toggle states for CC/BCC
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)

  // Rich text editor states
  const [showImageModal, setShowImageModal] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const [imageWidth, setImageWidth] = useState("300")
  const [imageHeight, setImageHeight] = useState("200")
  const [showHtmlModal, setShowHtmlModal] = useState(false)
  const [htmlContent, setHtmlContent] = useState("")
  const [htmlContentToInsert, setHtmlContentToInsert] = useState("")

  // Add useEffect to automatically clear htmlContentToInsert after insertion
  useEffect(() => {
    if (htmlContentToInsert) {
      // Clear after a short delay to allow the RichTextEditor to process it
      const timer = setTimeout(() => {
        setHtmlContentToInsert("")
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [htmlContentToInsert])
  const [editingImage, setEditingImage] = useState(null)

  // Determine if this is bulk email mode
  const isBulkMode = selectedLeads && selectedLeads.length > 0
  // Check multiple email field variations
  const validLeads = isBulkMode ? selectedLeads.filter(lead => {
    const email = lead.Email || lead.email || lead.EmailAddress || lead.emailAddress
    return email && email !== "N/A" && email.trim() !== ""
  }) : []


  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates()
      // Reset form when modal opens
      const initialToValue = isBulkMode
        ? `${validLeads.length} recipients selected`
        : (leadData?.email || "")

      const initialToEmails = isBulkMode
        ? validLeads.map(lead => lead.Email || lead.email || lead.EmailAddress || lead.emailAddress).filter(email => email && email !== "N/A")
        : []

      setEmailData({
        to: initialToValue,
        toEmails: initialToEmails,
        cc: [],
        bcc: [],
        subject: "",
        body: "",
        attachments: initialAttachments || []
      })
      setSelectedTemplate(null)
      setTemplateVariables({})
      setActiveTab(0)
      setShowHtmlEditor(false)
      setShowCc(false)
      setShowBcc(false)
      // Reset rich text editor states
      setShowImageModal(false)
      setImageUrl("")
      setImageWidth("300")
      setImageHeight("200")
      setShowHtmlModal(false)
      setHtmlContent("")
      setHtmlContentToInsert("")
      setEditingImage(null)
    } else {
      // Clean up search timeout when modal closes
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leadData, selectedLeads, isBulkMode])

  // Rich text editor helper functions
  const handleImageInsert = () => {
    if (imageUrl.trim()) {
      setShowImageModal(false)
      setTimeout(() => {
        setImageUrl("")
        setImageWidth("300")
        setImageHeight("200")
        setEditingImage(null)
      }, 100)
    }
  }

  const handleImageModalClose = () => {
    setShowImageModal(false)
    setImageUrl("")
    setImageWidth("300")
    setImageHeight("200")
    setEditingImage(null)
  }

  const handleImageEdit = (imageNode) => {
    setEditingImage(imageNode)
    setImageUrl(imageNode.attrs.src)
    setImageWidth(imageNode.attrs.width || "300")
    setImageHeight(imageNode.attrs.height || "200")
    setShowImageModal(true)
  }

  const handleHtmlInsert = () => {
    if (htmlContent.trim()) {
      // Trigger insertion by setting the content to insert
      setHtmlContentToInsert(htmlContent)
      // Close the modal and clear the modal content
      setShowHtmlModal(false)
      setHtmlContent("")
    }
  }

  const handleHtmlModalClose = () => {
    setShowHtmlModal(false)
    setHtmlContent("")
  }

  // Handle template selection and variable processing
  useEffect(() => {
    if (selectedTemplate) {
      const templateBody = selectedTemplate.body || selectedTemplate.Body || ''
      const templateSubject = selectedTemplate.subject || selectedTemplate.Subject || ''

      const variables = extractTemplateVariables(templateBody)
      const variableDefaults = {}

      variables.forEach(variable => {
        switch (variable.toLowerCase()) {
          case 'name':
          case 'leadname':
            variableDefaults[variable] = leadData?.name || ''
            break
          case 'email':
            variableDefaults[variable] = leadData?.email || ''
            break
          case 'mobile':
          case 'phone':
            variableDefaults[variable] = leadData?.mobile || leadData?.phone || ''
            break
          case 'company':
            variableDefaults[variable] = leadData?.company || ''
            break
          default:
            variableDefaults[variable] = ''
        }
      })

      setTemplateVariables(variableDefaults)

      // Keep HTML format for emails and process variables
      const processedBody = replaceTemplateVariables(templateBody, variableDefaults)

      setEmailData(prev => ({
        ...prev,
        subject: templateSubject,
        body: processedBody
      }))
    }
  }, [selectedTemplate, leadData])

  const loadTemplates = async (searchTerm = templateSearch) => {
    try {
      setTemplatesLoading(true)
      const result = await getTemplates({
        pageNumber: 1,
        pageSize: 50,
        channelId: 1, // Email channel ID (ChannelId: 1 is Email)
        search: searchTerm
      })
      if (result.success) {
        setTemplates(result.data)
      } else {
        toast.error(result.error || "Failed to load templates")
      }
    } catch (error) {
      console.error("Error loading templates:", error)
      toast.error("Failed to load templates")
    } finally {
      setTemplatesLoading(false)
    }
  }

  const handleTemplateSearch = (searchTerm) => {
    setTemplateSearch(searchTerm)
    // Clear existing timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout)
    }
    // Set new timeout
    window.searchTimeout = setTimeout(() => {
      loadTemplates(searchTerm)
    }, 300)
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setActiveTab(0) // Switch to compose tab

    // Automatically include template attachments if they exist
    if (template.attachments && template.attachments.length > 0) {
      // Map attachment objects to a format compatible with the file input
      const mappedAttachments = template.attachments.map(att => {
        // If it's already a File object, use it directly
        if (att instanceof File) {
          return att;
        }

        // Otherwise, create a reference object that can be used for display
        // Note: For actual file uploads, you may need to fetch the file from the URL
        return {
          name: att.FileName || att.fileName || 'Unknown File',
          path: att.FilePath || att.filePath,
          templateAttachment: true // Flag to identify template attachments
        };
      });

      setEmailData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...mappedAttachments]
      }));

      toast.success(`Template selected with ${template.attachments.length} attachment${template.attachments.length !== 1 ? 's' : ''}`);
    }
  }


  const handleVariableChange = (variable, value) => {
    const updatedVariables = { ...templateVariables, [variable]: value }
    setTemplateVariables(updatedVariables)

    if (selectedTemplate) {
      const templateBody = selectedTemplate.body || selectedTemplate.Body || ''
      const processedBody = replaceTemplateVariables(templateBody, updatedVariables)
      setEmailData(prev => ({
        ...prev,
        body: processedBody
      }))
    }
  }

  const proceedWithEmailSend = async (emailService) => {
    if (isBulkMode) {
      // Bulk email mode
      if (emailData.toEmails.length === 0) {
        toast.error("No email addresses selected")
        return
      }

      try {
        setIsSending(true)

        // Transform email addresses to match expected format
        // Find corresponding lead data for each email
        const leadsForEmail = emailData.toEmails.map(email => {
          const lead = validLeads.find(l => {
            const leadEmail = l.Email || l.email || l.EmailAddress || l.emailAddress
            return leadEmail === email
          })
          return {
            id: lead?.LeadId || lead?.leadId || lead?.id || lead?.userId || email, // Fallback to email if lead not found
            email: email,
            name: lead?.leadname || lead?.leadName || lead?.name || lead?.Name || email.split('@')[0]
          }
        })

        // Wrap body HTML in complete HTML document with head tags and styling
        const completeEmailHtml = ensureCompleteEmailHtml(emailData.body)

        const result = await sendEmailToMultipleLeads(
          leadsForEmail,
          emailData.subject,
          completeEmailHtml,
          emailData.cc,
          emailData.bcc,
          emailData.attachments,
          emailService?.ProviderCode,
          selectedTemplate?.TemplateId || selectedTemplate?.templateId
        )

        if (result.success) {
          toast.success(result.message || `Email sent to ${emailData.toEmails.length} recipients successfully!`)
          onClose()
        } else {
          throw new Error(result.message || "Failed to send emails")
        }
      } catch (error) {
        console.error("Error sending bulk email:", error)
        toast.error(error.message || "Failed to send emails")
      } finally {
        setIsSending(false)
      }
    } else {
      // Single email mode
      if (!emailData.to.trim()) {
        toast.error("Please enter recipient email")
        return
      }

      if (!leadData?.id) {
        toast.error("Lead ID is required to send email")
        return
      }

      try {
        setIsSending(true)

        // Wrap body HTML in complete HTML document with head tags and styling
        const completeEmailHtml = ensureCompleteEmailHtml(emailData.body)

        const result = await sendEmailToLead(
          leadData.id,
          emailData.to,
          emailData.subject,
          completeEmailHtml,
          emailData.cc,
          emailData.bcc,
          emailData.attachments,
          emailService?.ProviderCode,
          selectedTemplate?.TemplateId || selectedTemplate?.templateId
        )

        if (result.success) {
          toast.success(result.message || "Email sent successfully!")
          onClose()
        } else {
          throw new Error(result.message || "Failed to send email")
        }
      } catch (error) {
        console.error("Error sending email:", error)
        toast.error(error.message || "Failed to send email")
      } finally {
        setIsSending(false)
      }
    }
  }


  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.body.trim()) {
      toast.error("Please fill in all required fields")
      return
    }

    if (!selectedEmailService) {
      // Open email service selector modal
      setShowEmailServiceSelector(true)
      return
    }

    // Proceed with sending email
    await proceedWithEmailSend(selectedEmailService)
  }

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[200]" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">

              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {/* KEY FIX: Added h-full to DialogPanel and proper flex layout + remove outline */}
                <DialogPanel className="relative w-full max-w-2xl h-full max-h-[92vh] min-h-[610px] transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900 flex flex-col outline-none focus:outline-none">
                  {/* Header - Fixed */}
                  <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8 bg-blue-100 text-blue-700 text-sm font-medium">
                        <EnvelopeIcon className="w-4 h-4" />
                      </Avatar>
                      <div>
                        <DialogTitle as="h3" className="text-lg font-medium text-gray-900 dark:text-white">
                          {isBulkMode ? "Send Bulk Email" : "Send Email"}
                        </DialogTitle>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {isBulkMode
                            ? `${emailData.toEmails?.length || 0} recipients selected • Remove unwanted emails using × button`
                            : `${leadData?.name || "Lead Contact"} - ${leadData?.email || "No email"}`
                          }
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={onClose}
                      variant="flat"
                      isIcon
                      className="w-8 h-8 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Tab Navigation - Fixed */}
                  <div className="flex-shrink-0">
                    <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
                      <TabList className="flex border-b border-gray-200 dark:border-gray-700 px-6">
                        <Tab className={({ selected }) =>
                          `px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${selected
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }`
                        }>
                          Compose Message
                        </Tab>
                        <Tab className={({ selected }) =>
                          `px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${selected
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }`
                        }>
                          Choose Template
                        </Tab>
                      </TabList>

                      {/* KEY FIX: Tab Content with proper height constraints */}
                      <TabPanels className="flex-1 min-h-0 overflow-hidden">
                        {/* Compose Tab */}
                        <TabPanel className="h-full overflow-hidden">
                          <div className="h-full flex flex-col">
                            {/* KEY FIX: Scrollable Content with explicit height */}
                            <div className="flex-1 overflow-y-auto min-h-0" style={{ maxHeight: 'calc(92vh - 170px)' }}>
                              <div className="p-6 space-y-4">
                                {/* Template Selection Indicator */}
                                {selectedTemplate && (
                                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-2 mb-2">
                                      <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                        Using Template: {selectedTemplate.name || selectedTemplate.Name || 'Unnamed Template'}
                                      </span>
                                      <Button
                                        onClick={() => {
                                          setSelectedTemplate(null)
                                          setEmailData(prev => ({ ...prev, subject: '', body: '' }))
                                          setTemplateVariables({})
                                        }}
                                        variant="flat"
                                        size="sm"
                                        className="ml-auto text-xs"
                                      >
                                        Clear Template
                                      </Button>
                                    </div>

                                    {/* Template Variables */}
                                    {extractTemplateVariables(selectedTemplate.body || selectedTemplate.Body || '').length > 0 && (
                                      <div className="space-y-3">
                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                          Fill in the template variables:
                                        </p>
                                        <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                                          <div className="grid grid-cols-2 gap-3">
                                            {extractTemplateVariables(selectedTemplate.body || selectedTemplate.Body || '').map((variable) => (
                                              <div key={variable}>
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                  {variable}
                                                </label>
                                                <Input
                                                  value={templateVariables[variable] || ''}
                                                  onChange={(e) => handleVariableChange(variable, e.target.value)}
                                                  placeholder={`Enter ${variable}`}
                                                  className="text-sm"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Email Form */}
                                <div className="space-y-4">
                                  {isBulkMode ? (
                                    <EmailChipsInput
                                      label="To"
                                      emails={emailData.toEmails}
                                      onChange={(emails) => setEmailData(prev => ({ ...prev, toEmails: emails }))}
                                      placeholder="Lead email addresses will appear as chips here"
                                    />
                                  ) : (
                                    <Input
                                      label="To"
                                      value={emailData.to}
                                      onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                                      placeholder="Enter recipient email"
                                      disabled={!!leadData?.email}
                                      readOnly={!!leadData?.email}
                                    />
                                  )}

                                  {/* CC/BCC Toggle Buttons */}
                                  {(!showCc || !showBcc) && (
                                    <div className="flex gap-2">
                                      {!showCc && (
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          size="sm"
                                          onClick={() => setShowCc(true)}
                                          className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                          <PlusIcon className="w-3 h-3 mr-1" />
                                          Add CC
                                        </Button>
                                      )}
                                      {!showBcc && (
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          size="sm"
                                          onClick={() => setShowBcc(true)}
                                          className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                          <PlusIcon className="w-3 h-3 mr-1" />
                                          Add BCC
                                        </Button>
                                      )}
                                    </div>
                                  )}

                                  {/* CC Field */}
                                  {showCc && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                          CC (Carbon Copy)
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setShowCc(false)
                                            setEmailData(prev => ({ ...prev, cc: [] }))
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors flex items-center gap-1"
                                        >
                                          <XMarkIcon className="w-3 h-3" />
                                          Remove CC
                                        </button>
                                      </div>
                                      <EmailChipsInput
                                        label=""
                                        emails={emailData.cc}
                                        onChange={(emails) => setEmailData(prev => ({ ...prev, cc: emails }))}
                                        placeholder="Add CC recipients (press Enter or comma)"
                                      />
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        CC recipients will see each other&apos;s email addresses
                                      </p>
                                    </div>
                                  )}

                                  {/* BCC Field */}
                                  {showBcc && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                          BCC (Blind Carbon Copy)
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setShowBcc(false)
                                            setEmailData(prev => ({ ...prev, bcc: [] }))
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors flex items-center gap-1"
                                        >
                                          <XMarkIcon className="w-3 h-3" />
                                          Remove BCC
                                        </button>
                                      </div>
                                      <EmailChipsInput
                                        label=""
                                        emails={emailData.bcc}
                                        onChange={(emails) => setEmailData(prev => ({ ...prev, bcc: emails }))}
                                        placeholder="Add BCC recipients (press Enter or comma)"
                                      />
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        BCC recipients are hidden from other recipients
                                      </p>
                                    </div>
                                  )}

                                  <Input
                                    label="Subject"
                                    value={emailData.subject}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                                    placeholder="Enter email subject"
                                  />

                                  {/* File Attachments */}
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Attachments {emailData.attachments.length > 0 && (
                                          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                            ({emailData.attachments.length} file{emailData.attachments.length !== 1 ? 's' : ''})
                                          </span>
                                        )}
                                      </label>
                                      {emailData.attachments.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setEmailData(prev => ({ ...prev, attachments: [] }))}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                                        >
                                          Clear All
                                        </button>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <input
                                        type="file"
                                        multiple
                                        onChange={(e) => {
                                          const files = Array.from(e.target.files || [])
                                          if (files.length > 0) {
                                            setEmailData(prev => ({
                                              ...prev,
                                              attachments: [...prev.attachments, ...files]
                                            }))
                                            toast.success(`${files.length} file${files.length !== 1 ? 's' : ''} added`)
                                          }
                                          e.target.value = '' // Reset input
                                        }}
                                        className="hidden"
                                        id="file-upload"
                                      />
                                      <label
                                        htmlFor="file-upload"
                                        className="cursor-pointer inline-flex items-center px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-blue-50 hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:border-blue-500 transition-all duration-200"
                                      >
                                        <PaperClipIcon className="w-4 h-4 mr-2" />
                                        Choose Files
                                      </label>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        or drag and drop files here
                                      </span>
                                    </div>

                                    {/* Attachment List */}
                                    {emailData.attachments.length > 0 && (
                                      <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                                        {emailData.attachments.map((file, index) => (
                                          <div
                                            key={index}
                                            className="group flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm transition-all duration-200"
                                          >
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <PaperClipIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                                                  {file.name || file}
                                                </p>
                                                {/* <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                                                </p> */}
                                              </div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEmailData(prev => ({
                                                  ...prev,
                                                  attachments: prev.attachments.filter((_, i) => i !== index)
                                                }))
                                                toast.success('File removed')
                                              }}
                                              className="ml-3 flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                                              title="Remove file"
                                            >
                                              <XMarkIcon className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      💡 Tip: You can attach multiple files at once
                                    </p>
                                  </div>

                                  {/* Rich Text Editor for Message */}
                                  <div className="space-y-2">
                                    <RichTextEditor
                                      label="Message"
                                      value={emailData.body}
                                      onChange={(content) => setEmailData(prev => ({ ...prev, body: content }))}
                                      placeholder="Compose your professional email with rich formatting..."
                                      height="300px"
                                      onImageInsert={() => setShowImageModal(true)}
                                      imageUrlToInsert={imageUrl}
                                      imageWidth={imageWidth}
                                      imageHeight={imageHeight}
                                      onImageEdit={handleImageEdit}
                                      editingImage={editingImage}
                                      onHtmlInsert={() => {
                                        setHtmlContent("")
                                        setShowHtmlModal(true)
                                      }}
                                      htmlContentToInsert={htmlContentToInsert}
                                    />
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {emailData.body.replace(/<[^>]*>/g, '').length} characters (HTML content)
                                    </div>
                                  </div>
                                </div>

                              </div>
                            </div>

                            {/* Send Button - Only visible in Compose tab */}
                            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-3">
                              <div className="flex justify-end items-center">
                                <Button
                                  onClick={handleSendEmail}
                                  disabled={(!isBulkMode && !emailData.to.trim()) || !emailData.subject.trim() || !emailData.body.trim() || isSending || (isBulkMode && emailData.toEmails.length === 0)}
                                  variant="solid"
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                                >
                                  {isSending ? (
                                    <>
                                      <Spinner className="w-4 h-4 mr-2" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                                      {isBulkMode ? `Send to ${emailData.toEmails.length} Recipients` : "Send Email"}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </TabPanel>

                        {/* Templates Tab */}
                        <TabPanel className="h-full overflow-hidden">
                          <div className="h-full flex flex-col p-6">
                            {/* Search - Fixed */}
                            <div className="flex-shrink-0 mb-4">
                              <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                  value={templateSearch}
                                  onChange={(e) => handleTemplateSearch(e.target.value)}
                                  placeholder="Search email templates..."
                                  className="pl-10"
                                />
                              </div>
                            </div>

                            {/* Templates List - Clean Design */}
                            <div className="flex-1 overflow-y-auto min-h-0" style={{ maxHeight: 'calc(92vh - 240px)', minHeight: '360px' }}>
                              {templatesLoading ? (
                                <div className="flex items-center justify-center py-8 h-full">
                                  <div className="text-center">
                                    <Spinner className="h-6 w-6 mx-auto mb-2" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Loading templates...</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3 pb-4 min-h-full">
                                  {templates.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                      <div className="text-center py-8">
                                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                          No templates found
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                          {templateSearch ? "Try a different search term" : "No email templates available"}
                                        </p>
                                      </div>
                                    </div>
                                  ) : (
                                    templates.map((template) => (
                                      <div
                                        key={template.id}
                                        className="border border-gray-200 bg-white rounded-md p-3 hover:border-blue-500 hover:bg-blue-50/50 transition-colors dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-blue-900/10"
                                      >
                                        {/* Header with title and button */}
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                              {template.name || template.Name || 'Unnamed Template'}
                                            </h4>
                                          </div>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleTemplateSelect(template)
                                            }}
                                            variant="outlined"
                                            size="sm"
                                            className="flex-shrink-0 px-2 py-1 text-xs"
                                          >
                                            Use Template
                                          </Button>
                                        </div>

                                        {/* Subject line */}
                                        {(template.subject || template.Subject) && (
                                          <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                              <span className="font-medium text-gray-900 dark:text-gray-100">Subject:</span> {template.subject || template.Subject}
                                            </p>
                                          </div>
                                        )}

                                        {/* Body preview - sandboxed to prevent global CSS leakage */}
                                        <div className="mb-2">
                                          <IframePreview html={template.body || template.Body || ''} />
                                        </div>

                                        {/* Template Attachments Display - NEW SECTION */}
                                        {/* Template Attachments Display - FIXED VERSION */}
                                        {template.attachments && template.attachments.length > 0 && (
                                          <div className="mb-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2 mb-2">
                                              <PaperClipIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                {template.attachments.length} Attachment{template.attachments.length !== 1 ? 's' : ''}
                                              </span>
                                            </div>
                                            <div className="space-y-1.5">
                                              {template.attachments.map((attachment, idx) => {
                                                // Safely extract values to prevent object rendering errors
                                                const fileName = attachment.FileName || attachment.fileName || `File ${idx + 1}`;
                                                const fileSize = attachment.FileSize || attachment.fileSize;

                                                return (
                                                  <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                                  >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
                                                        <PaperClipIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                                      </div>
                                                      <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                                          {fileName}
                                                        </p>
                                                        {fileSize && (
                                                          <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                                            {(fileSize / 1024).toFixed(1)} KB
                                                          </p>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}


                                        {/* Variables badges */}
                                        {extractTemplateVariables(template.body || template.Body || '').length > 0 && (
                                          <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-200 dark:border-gray-700">
                                            {extractTemplateVariables(template.body || template.Body || '').map((variable) => (
                                              <Badge key={variable} variant="soft" color="primary" className="text-[10px]">
                                                {variable}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </TabPanel>
                      </TabPanels>
                    </TabGroup>
                  </div>

                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Image Insert Modal */}
      <Transition appear show={showImageModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9999]"
          onClose={() => { }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex w-full max-w-md origin-center flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-gray-800 outline-none focus:outline-none">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5 dark:bg-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 shadow-md dark:bg-blue-900">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle
                      as="h3"
                      className="text-base font-medium text-gray-800 dark:text-gray-100"
                    >
                      {editingImage ? 'Edit Image' : 'Insert Image'}
                    </DialogTitle>
                  </div>
                  <Button
                    onClick={handleImageModalClose}
                    variant="ghost"
                    size="sm"
                    className="rounded-full p-1"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </Button>
                </div>

                {/* Body */}
                <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                    Enter the image URL and dimensions.
                  </p>

                  <div className="space-y-4">
                    <Input
                      label="Image URL"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      type="url"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Width (px)"
                        value={imageWidth}
                        onChange={(e) => setImageWidth(e.target.value)}
                        placeholder="300"
                        type="number"
                      />
                      <Input
                        label="Height (px)"
                        value={imageHeight}
                        onChange={(e) => setImageHeight(e.target.value)}
                        placeholder="200"
                        type="number"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleImageModalClose}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleImageInsert}
                      disabled={!imageUrl.trim()}
                      color="primary"
                    >
                      {editingImage ? 'Update' : 'Insert'} Image
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* HTML Content Modal */}
      <Transition appear show={showHtmlModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9999]"
          onClose={() => { }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex w-full max-w-2xl origin-center flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-gray-800 outline-none focus:outline-none">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5 dark:bg-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 shadow-md dark:bg-blue-900">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle
                      as="h3"
                      className="text-base font-medium text-gray-800 dark:text-gray-100"
                    >
                      Insert HTML Content
                    </DialogTitle>
                  </div>
                  <Button
                    onClick={handleHtmlModalClose}
                    variant="ghost"
                    size="sm"
                    className="rounded-full p-1"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </Button>
                </div>

                {/* Body */}
                <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                    Enter your HTML content to insert into the email.
                  </p>

                  <div className="space-y-4">
                    <Textarea
                      label="HTML Content"
                      value={htmlContent}
                      onChange={(e) => setHtmlContent(e.target.value)}
                      placeholder="<p>Enter your HTML content here...</p>"
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleHtmlModalClose}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleHtmlInsert}
                      disabled={!htmlContent.trim()}
                      color="primary"
                    >
                      Insert HTML
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Email Service Selector Modal */}
      <EmailServiceSelector
        isOpen={showEmailServiceSelector}
        onClose={() => setShowEmailServiceSelector(false)}
        onSelect={(service) => {
          setSelectedEmailService(service)
          // Automatically proceed with sending after service selection
          setTimeout(() => {
            proceedWithEmailSend(service)
          }, 100)
        }}
        selectedService={selectedEmailService}
        title="Select Email Service"
        description="Choose which email service to use for sending this message"
      />
    </>
  )
}

export default EmailMessageModal
